/**
 * Auth store.
 *
 * The access token lives in memory only. The refresh token is the sole persisted
 * credential, and on the native shells it goes into the OS keychain via Tauri's store
 * plugin rather than into `localStorage`.
 *
 * The predecessor kept a 24-hour token in `localStorage`, which is both long-lived and
 * reachable from any injected script. Splitting the pair means an XSS window yields a
 * token that expires in an hour rather than a day, and the durable credential is behind
 * a platform boundary on native.
 */

import { create } from "zustand";

import { configureAuth } from "@/api/client";
import { secureStorage } from "@/platform/storage";

const REFRESH_KEY = "quantlab.refresh";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  hydrated: boolean;
  setTokens: (tokens: { access: string; refresh: string }) => void;
  logout: () => void;
  hydrate: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  hydrated: false,

  setTokens: ({ access, refresh }) => {
    set({ accessToken: access, refreshToken: refresh });
    void secureStorage.set(REFRESH_KEY, refresh);
  },

  logout: () => {
    set({ accessToken: null, refreshToken: null });
    void secureStorage.remove(REFRESH_KEY);
  },

  hydrate: async () => {
    if (get().hydrated) return;
    const refresh = await secureStorage.get(REFRESH_KEY);
    // Only the refresh token is restored. The client exchanges it for an access token
    // on the first 401, which happens on the first request.
    set({ refreshToken: refresh, hydrated: true });
  },
}));

// The client is injected rather than importing this store, so it stays testable without
// a React tree and this file stays the only place that knows where tokens live.
configureAuth(
  () => {
    const { accessToken, refreshToken } = useAuthStore.getState();
    return { access: accessToken, refresh: refreshToken };
  },
  (tokens) => useAuthStore.getState().setTokens(tokens),
  () => useAuthStore.getState().logout(),
);

export function useIsAuthenticated(): boolean {
  return useAuthStore((state) => Boolean(state.accessToken || state.refreshToken));
}
